import React, { useState, useMemo, useEffect, useCallback } from "react";
import { X, Search, User, Building, Mail, Check, Plus } from "lucide-react";
import Button from "../common/Button/Button";
import PaginationControls from "../common/Pagination/PaginationControls";
import customerService from "../../api/crm/customers/customerService";
import {
  FIELD_LIMITS,
  handleSearchInputChange
} from "../../utils/inputValidation";

const CustomerSelectionModal = ({
  isOpen,
  onClose,
  onSelect,
  title = "Select Customer",
  description = "Choose a customer to log activity for",
  confirmText = "Log Activity"
}) => {
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerPagination, setCustomerPagination] = useState({});
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [debouncedCustomerSearchTerm, setDebouncedCustomerSearchTerm] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customerPage, setCustomerPage] = useState(1);
  const [customerLimit, setCustomerLimit] = useState(10);

  useEffect(() => {
    if (!isOpen) {
      setCustomerPage(1);
      setCustomerSearchTerm("");
      setSelectedCustomerId("");
      setDebouncedCustomerSearchTerm("");
    }
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomerSearchTerm(customerSearchTerm);
      setCustomerPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [customerSearchTerm]);

  const loadCustomers = useCallback(async () => {
    setCustomersLoading(true);
    try {
      const filters = {
        page: customerPage,
        limit: customerLimit,
      };

      if (debouncedCustomerSearchTerm.trim()) {
        filters.search = debouncedCustomerSearchTerm.trim();
      }

      const result = await customerService.getCustomers(filters);
      if (result.success) {
        setCustomers(result.customers || []);
        setCustomerPagination(result.pagination || {});
      } else {
        setCustomers([]);
        setCustomerPagination({});
      }
    } catch (error) {
      setCustomers([]);
      setCustomerPagination({});
    } finally {
      setCustomersLoading(false);
    }
  }, [customerPage, customerLimit, debouncedCustomerSearchTerm]);

  useEffect(() => {
    if (isOpen) {
      loadCustomers();
    }
  }, [isOpen, loadCustomers]);

  const displayedCustomers = useMemo(() => {
    if (!customers || customers.length === 0) return [];
    return [...customers].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.updatedAt || 0);
      const dateB = new Date(b.createdAt || b.updatedAt || 0);
      return dateB - dateA;
    });
  }, [customers]);

  const getCustomerName = (customer) => {
    if (!customer) return "";
    return customer.companyName || customer.contactPerson || customer.customerId || "Unnamed Customer";
  };

  const getCustomerEmail = (customer) => {
    if (!customer) return "";
    return customer.email || customer.primaryEmail || customer.contactEmail || "";
  };

  const getCustomerAddress = (customer) => {
    if (!customer) return "";

    const formatSingleAddress = (addr) => {
      if (!addr) return "";

      if (typeof addr === "string") {
        const trimmed = addr.trim();
        if (!trimmed) return "";
        if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
          try {
            const parsed = JSON.parse(trimmed);
            return formatSingleAddress(parsed);
          } catch (e) {
            const streetMatch = trimmed.match(/street:\s*['"]([^'"]*)['"]/);
            const cityMatch = trimmed.match(/city:\s*['"]([^'"]*)['"]/);
            const stateMatch = trimmed.match(/state:\s*['"]([^'"]*)['"]/);
            const pincodeMatch = trimmed.match(/pincode:\s*['"]([^'"]*)['"]/);
            const countryMatch = trimmed.match(/country:\s*['"]([^'"]*)['"]/);

            const parts = [];
            if (streetMatch && streetMatch[1]?.trim()) parts.push(streetMatch[1].trim());
            if (cityMatch && cityMatch[1]?.trim()) parts.push(cityMatch[1].trim());
            if (stateMatch && stateMatch[1]?.trim()) parts.push(stateMatch[1].trim());
            if (pincodeMatch && pincodeMatch[1]?.trim()) parts.push(pincodeMatch[1].trim());
            if (countryMatch && countryMatch[1]?.trim() && countryMatch[1].trim().toLowerCase() !== "india") {
              parts.push(countryMatch[1].trim());
            }
            if (parts.length > 0) return parts.join(", ");
            return trimmed.replace(/[{}\[\]"']/g, "").trim();
          }
        }
        return trimmed;
      }

      if (typeof addr === "object" && addr !== null) {
        if (Array.isArray(addr)) {
          return addr.map(formatSingleAddress).filter(Boolean).join(", ");
        }

        const parts = [];

        // Street lines
        const street = addr.street || addr.address || addr.addressLine1 || addr.street1 || "";
        if (typeof street === "string" && street.trim() && !street.trim().startsWith("{")) {
          parts.push(street.trim());
        } else if (typeof street === "object" && street !== null) {
          const nested = formatSingleAddress(street);
          if (nested) parts.push(nested);
        }

        if (typeof addr.street2 === "string" && addr.street2.trim() && !addr.street2.trim().startsWith("{")) {
          parts.push(addr.street2.trim());
        }
        if (typeof addr.street3 === "string" && addr.street3.trim() && !addr.street3.trim().startsWith("{")) {
          parts.push(addr.street3.trim());
        }

        // City
        const cityVal = typeof addr.city === "object" && addr.city !== null
          ? (addr.city.name || addr.city.label || "")
          : addr.city;
        if (
          typeof cityVal === "string" &&
          cityVal.trim() &&
          !cityVal.trim().startsWith("{") &&
          cityVal.trim().toLowerCase() !== "not provided"
        ) {
          parts.push(cityVal.trim());
        }

        // State
        const stateVal = typeof addr.state === "object" && addr.state !== null
          ? (addr.state.name || addr.state.label || "")
          : addr.state;
        if (
          typeof stateVal === "string" &&
          stateVal.trim() &&
          !stateVal.trim().startsWith("{") &&
          stateVal.trim().toLowerCase() !== "not provided"
        ) {
          parts.push(stateVal.trim());
        }

        // Pincode / Postal Code
        const pincodeVal = addr.pincode || addr.pinCode || addr.postalCode || addr.pin || "";
        const pincodeStr = typeof pincodeVal === "object" && pincodeVal !== null
          ? (pincodeVal.name || "")
          : String(pincodeVal || "").trim();
        if (
          pincodeStr &&
          pincodeStr !== "000000" &&
          pincodeStr !== "0" &&
          !pincodeStr.startsWith("{")
        ) {
          parts.push(pincodeStr);
        }

        // Country
        const countryVal = typeof addr.country === "object" && addr.country !== null
          ? (addr.country.name || addr.country.label || "")
          : addr.country;
        if (
          typeof countryVal === "string" &&
          countryVal.trim() &&
          !countryVal.trim().startsWith("{") &&
          countryVal.trim().toLowerCase() !== "not provided" &&
          countryVal.trim().toLowerCase() !== "india"
        ) {
          parts.push(countryVal.trim());
        }

        return parts.filter(Boolean).join(", ");
      }

      return "";
    };

    const billing = formatSingleAddress(customer.billingAddress);
    if (billing) return billing;

    const shipping = formatSingleAddress(customer.shippingAddress);
    if (shipping) return shipping;

    const direct = formatSingleAddress(customer.address);
    if (direct) return direct;

    if (Array.isArray(customer.locations) && customer.locations.length > 0) {
      for (const loc of customer.locations) {
        const locAddr = formatSingleAddress(loc?.address || loc);
        if (locAddr) return locAddr;
      }
    }

    return "";
  };

  const handleConfirm = () => {
    const selectedCustomer = customers.find(
      (customer) => (customer.id || customer._id || customer.customerId) === selectedCustomerId
    );
    if (selectedCustomer) {
      onSelect(selectedCustomerId, selectedCustomer);
    }
  };

  const currentPage = customerPagination.currentPage || customerPagination.page || customerPage;
  const totalPages = customerPagination.totalPages || 1;
  const totalItems = customerPagination.totalCustomers || customerPagination.total || customerPagination.totalItems || 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-600 text-sm mt-1">{description}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search customers by name, company, or email..."
              value={customerSearchTerm}
              onChange={(e) => {
                const sanitized = handleSearchInputChange(
                  e,
                  setCustomerSearchTerm,
                  FIELD_LIMITS.search
                );
                setCustomerSearchTerm(sanitized);
              }}
              maxLength={FIELD_LIMITS.search}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
            />
          </div>
        </div>

        <div
          className="flex-1 overflow-y-auto p-6"
          style={{ maxHeight: "420px" }}
        >
          {customersLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                <p className="text-gray-600">Loading customers...</p>
              </div>
            </div>
          ) : displayedCustomers.length === 0 ? (
            <div className="text-center py-12">
              <div className="p-3 bg-gray-100 rounded-full mb-4 inline-block">
                <User className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No customers found
              </h3>
              <p className="text-gray-500">
                {customerSearchTerm
                  ? "Try adjusting your search criteria."
                  : "No customers available. Please add customers first."}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {displayedCustomers.map((customer) => {
                const customerId = customer.id || customer._id || customer.customerId;
                const isSelected = selectedCustomerId === customerId;
                const customerEmail = getCustomerEmail(customer);
                const customerReferenceId = customer.customerId || "";
                const customerAddress = getCustomerAddress(customer);

                const companyName = customer.companyName || getCustomerName(customer) || "N/A";
                const showContactPerson =
                  customer.contactPerson &&
                  customer.contactPerson.trim().toLowerCase() !== companyName.trim().toLowerCase();

                return (
                  <div
                    key={customerId}
                    onClick={() => {
                      setSelectedCustomerId(isSelected ? "" : customerId);
                    }}
                    className={`px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? "border-agrosaf-green bg-agrosaf-green/10"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`p-1.5 rounded-md flex-shrink-0 ${
                          isSelected
                            ? "bg-agrosaf-green text-white"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        <User className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0 grid grid-cols-3 gap-x-4 items-start">
                        <div className="min-w-0">
                          <span className="text-[11px] font-medium text-gray-500 leading-none">Customer</span>
                          <p className="text-sm font-semibold text-gray-900 capitalize leading-snug mt-0.5 break-words whitespace-normal">
                            {companyName}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <span className="text-[11px] font-medium text-gray-500 leading-none">Address</span>
                          <p
                            className="text-sm text-gray-600 capitalize leading-snug mt-0.5 break-words whitespace-normal"
                            title={customerAddress || "N/A"}
                          >
                            {customerAddress || "N/A"}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <span className="text-[11px] font-medium text-gray-500 leading-none">Customer ID</span>
                          <p
                            className="text-sm text-gray-700 font-mono truncate leading-tight mt-0.5"
                            title={customerReferenceId || "N/A"}
                          >
                            {customerReferenceId || "N/A"}
                          </p>
                        </div>
                      </div>
                      {isSelected ? (
                        <div className="flex-shrink-0">
                          <div className="w-4 h-4 rounded-full bg-agrosaf-green flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-white" />
                          </div>
                        </div>
                      ) : (
                        <div className="w-4 flex-shrink-0" />
                      )}
                    </div>
                    {(showContactPerson || customerEmail) && (
                      <div className="flex items-center gap-4 ml-8 mt-1 min-w-0 overflow-hidden text-xs text-gray-500">
                        {showContactPerson && (
                          <div className="flex items-center gap-1 min-w-0">
                            <Building className="h-3 w-3 flex-shrink-0" />
                            <span
                              className="truncate first-letter:capitalize"
                              title={customer.contactPerson}
                            >
                              {customer.contactPerson}
                            </span>
                          </div>
                        )}
                        {customerEmail && (
                          <div className="flex items-center gap-1 min-w-0 flex-1">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate lowercase" title={customerEmail}>
                              {customerEmail}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 pb-4">
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            limit={customerPagination.limit || customerLimit}
            hasNextPage={customerPagination.hasNextPage ?? currentPage < totalPages}
            hasPrevPage={customerPagination.hasPrevPage ?? currentPage > 1}
            onPageChange={(page) => setCustomerPage(page)}
            onLimitChange={(limit) => {
              setCustomerLimit(limit);
              setCustomerPage(1);
            }}
            limitOptions={[5, 10, 20, 50, 100]}
          />
        </div>

        <div className="flex justify-end items-center space-x-3 p-6 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={handleConfirm}
            disabled={!selectedCustomerId}
            className="bg-agrosaf-green hover:bg-agrosaf-darkGreen disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomerSelectionModal;
